import { LogLevel } from '../constants/log.constants';
import { TemplateDescriptions } from '../constants/templates.constants';

import { LogEntry } from '../interfaces/log-entry.interface';
import { LoggedInUserProfile } from '../interfaces/logged-in-user-profile.interface';
import { ISyncLogEntry } from '../interfaces/sync-log-entry.interface';
import { DiagnosticExportResult } from '../interfaces/messaging.interface';
import { MetadataExportResult } from '../interfaces/metadata-export-result.interface';
import { LogExportResult } from '../interfaces/log-export-result.interface';
import { StoreExportResult } from '../interfaces/store-export-result.interface';

import { DateTimeHelper } from './date-time-helper';
import { FileArchive, FileHelper } from './file-helper';
import { Linq } from './linq';

export class DiagnosticArchiver {

  static async archiveDiagnostics(
    loggedInUserProfile: LoggedInUserProfile,
    webSiteLogEntries: LogEntry[],
    syncLogEntries: ISyncLogEntry[],
    diagnosticExportResult: DiagnosticExportResult): Promise<{ zipFileName: string, zipBlob: Blob, error: string }> {

    const { metadataExportResult, logExportResult, storeExportResult, error } = diagnosticExportResult;

    const currentTimestamp = DateTimeHelper.now().toFormat('yyyy-MM-dd.HH-mm-ss-SSS');
    const zipFileName = FileHelper.sanitiseFileName(`Diagnostics.${loggedInUserProfile.emailAddress}.${currentTimestamp}.zip`);

    const archive: FileArchive = {};
    this.addMetadataToArchive(archive, metadataExportResult, loggedInUserProfile);
    this.addLogsToArchive(archive, logExportResult, webSiteLogEntries);
    this.addSyncLogsToArchive(archive, syncLogEntries);
    this.addIndexedDbToArchive(archive, storeExportResult);
    const zipBlob = await FileHelper.generateZippedBlob(archive);

    return { zipFileName, zipBlob, error };
  }

  private static addMetadataToArchive(
    archive: FileArchive,
    metadataExportResult: MetadataExportResult,
    loggedInUserProfile: LoggedInUserProfile): void {

    const { systemInformation } = metadataExportResult;
    const metadata = { systemInformation, loggedInUserProfile };

    const metadataBlob = FileHelper.createJsonBlob(metadata);
    archive[`Metadata.json`] = metadataBlob;
  }

  private static addLogsToArchive(
    archive: FileArchive,
    logExportResult: LogExportResult,
    webSiteLogEntries: LogEntry[]): void {

    const { logEntries: serviceWorkerLogEntries } = logExportResult;
    const logEntries = Linq.orderBy([...serviceWorkerLogEntries, ...webSiteLogEntries], item => item.timestamp, (key1: Date, key2: Date) => key1 > key2, true);

    const dataRows = logEntries
      .map(e => ({
        'TIMESTAMP': e.timestamp,
        'LOG LEVEL': LogLevel[e.logLevel],
        'MESSAGE': e.message
      }));

    const logsBlob = FileHelper.createXlsxBlob(dataRows, 'ConsoleLogs');
    archive[`Logs/ConsoleLogs.xlsx`] = logsBlob;
  }

  private static addSyncLogsToArchive(
    archive: FileArchive,
    syncLogEntries: ISyncLogEntry[]): void {

    const dataRows = syncLogEntries
      .map(e => ({
        'TIMESTAMP': e.timestamp,
        'OPERATION NAME': e.operationName,
        'LOG LEVEL': LogLevel[e.logLevel],
        'MESSAGE': e.message,
        'DETAILS': e.details
      }));

    const logsBlob = FileHelper.createXlsxBlob(dataRows, 'SyncLogs');
    archive[`Logs/SyncLogs.xlsx`] = logsBlob;
  }

  private static addIndexedDbToArchive(
    archive: FileArchive,
    storeExportResult: StoreExportResult): void {

    const { rawData: storeExportData, formData: storeExportFormData, files: storeExportFiles } = storeExportResult;
    const { name, version, data } = storeExportData;
    const { formsByTemplate } = storeExportFormData;

    const indexedDbBlob = FileHelper.createJsonBlob({ name, version });
    archive[`IndexedDB/IndexedDB.json`] = indexedDbBlob;

    for (const [objectStoreName, records] of Object.entries(data)) {
      const fileName = `${FileHelper.sanitiseFileName(objectStoreName)}.json`;
      const blob = FileHelper.createJsonBlob(records);
      archive[`IndexedDB/ObjectStores/${fileName}`] = blob;
    }

    for (const [template, forms] of formsByTemplate.entries()) {
      const templateDescription = TemplateDescriptions[template] ?? `Template ${template}`;
      const fileName = `${FileHelper.sanitiseFileName(templateDescription)}.json`;
      const blob = FileHelper.createJsonBlob(forms);
      archive[`IndexedDB/Forms/${fileName}`] = blob;
    }

    for (const { sourceId, id, name, blob } of storeExportFiles) {
      const folderName = FileHelper.sanitiseFileName(sourceId);
      const fileName = FileHelper.sanitiseFileName(`${id}.${(name || `missing-file-name-${(blob.type || 'unknown-type')}`)}`);
      archive[`IndexedDB/Files/${folderName}/${fileName}`] = blob;
    }
  }
}
